import React from "react";
import { useTranslation } from "react-i18next";
import { compareAsc, parseISO } from "date-fns";
import { CircularProgress, ScrollShadow } from "@nextui-org/react";
import { BsChatLeftText } from "react-icons/bs";
import { IoMdSend } from "react-icons/io";
import CommentDisplay from "../components/CommentDisplay";
import { useAppContext } from "../../../contexts/AppContext";
import { formatTimecode } from "../../../components/MAudioPlayer/timecode";

const ActivityPanel = ({
  comments,
  is_loading,
  is_posting,
  comment_input_text,
  set_comment_input_text,
  on_post_comment,
  on_seek,
  fetch_comments,
  item_id,
}) => {
  const { t } = useTranslation();
  const { popupAudioTime, popupCallId } = useAppContext();

  // A new comment is pinned to where the call's player currently is, like
  // debrief comments; nothing is attached before playback has started.
  const current_audio_timestamp =
    popupCallId === item_id && Number.isFinite(popupAudioTime) && popupAudioTime > 0
      ? popupAudioTime
      : null;

  const sorted_comments = (comments || []).slice().sort((a, b) => {
    const date_a = parseISO(a?.created_at);
    const date_b = parseISO(b?.created_at);
    return compareAsc(date_a, date_b);
  });

  const post_comment = () => {
    if (is_posting) return;
    on_post_comment(item_id, comment_input_text, current_audio_timestamp);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-3">
        <p className="text-sm font-medium text-gray-500">{t("Messages")}</p>
      </div>
      <ScrollShadow
        hideScrollBar
        className="flex-1 px-4 py-3 overflow-y-auto"
      >
        {!is_loading &&
          sorted_comments.map((comment) => (
            <CommentDisplay
              key={comment.id}
              comment={comment}
              fetchComments={() => fetch_comments(item_id)}
              onSeek={on_seek}
            />
          ))}
        {is_loading && (
          <div className="flex items-center justify-center py-8">
            <CircularProgress
              size="sm"
              aria-label="Loading..."
              color="primary"
            />
          </div>
        )}
        {!is_loading && sorted_comments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <BsChatLeftText
              size={32}
              className="mb-2"
            />
            <p className="text-sm">{t("Aucun message")}</p>
          </div>
        )}
      </ScrollShadow>
      <div className="px-4 py-3 border-t border-gray-200">
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2">
          <input
            className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
            placeholder={t("Message")}
            value={comment_input_text}
            onChange={(e) => set_comment_input_text(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") post_comment();
            }}
          />
          {current_audio_timestamp !== null && (
            <span
              className="text-xs text-gray-400 whitespace-nowrap"
              title={t("Le message sera lié à ce moment de l'appel")}
            >
              {t("à")} {formatTimecode(current_audio_timestamp)}
            </span>
          )}
          <button
            type="button"
            onClick={post_comment}
            disabled={is_posting}
            aria-label={t("Envoyer")}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {is_posting ? (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                classNames={{ svg: "w-[18px] h-[18px]" }}
              />
            ) : (
              <IoMdSend
                size={18}
                className="text-gray-500"
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityPanel;
